import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { ChevronLeft, Send, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

const PrecorChat = () => {
    const [messages, setMessages] = useState([
        { type: 'bot', text: "<b>Welcome to Precor Tech Support.</b><br><br>I'm here to help you troubleshoot equipment issues or find manual specifications. What's on your mind?" }
    ]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const chatEndRef = useRef(null);
    const navigate = useNavigate();

    const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    useEffect(() => { scrollToBottom() }, [messages, isThinking]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userQuery = input;
        setMessages(prev => [...prev, { type: 'user', text: userQuery }]);
        setInput('');
        setIsThinking(true);

        try {
            const response = await fetch("https://noncultivable-biogenetically-addyson.ngrok-free.dev/askgroq", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    query: userQuery, 
                    history: messages.map(m => ({ author_type: m.type, text: m.text })) 
                })
            });

            const data = await response.json();
            setMessages(prev => [...prev, { 
                type: 'bot', 
                text: data.answer, 
                sources: data.sources 
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { type: 'bot', text: "<b>Error:</b> Backend Offline." }]);
        } finally {
            setIsThinking(false);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-slate-100 font-sans">
            {/* Header */}
            <header className="bg-[#002C5F] text-white p-4 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/marketplace')} className="text-white">
                        <ChevronLeft /> Back
                    </Button>
                    <div className="w-10 h-10 bg-white text-[#002C5F] rounded flex items-center justify-center font-bold text-xl">P</div>
                    <div>
                        <h1 className="font-bold text-lg">Precor Support Assistant</h1>
                        <span className="text-xs text-green-400 flex items-center gap-1">● AI Agent Active</span>
                    </div>
                </div>
            </header>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-4 rounded-2xl shadow-sm ${
                            msg.type === 'user' ? 'bg-[#0066CC] text-white rounded-br-none' : 'bg-white text-slate-800 border rounded-bl-none'
                        }`}>
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.text) }} />
                            {msg.sources && (
                                <div className="mt-3 pt-2 border-t text-xs opacity-70">
                                    SOURCES: {msg.sources.map((s, idx) => (
                                        <a key={idx} href={s} className="text-blue-500 block underline">{s}</a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {isThinking && (
                    <div className="bg-white p-3 rounded-lg w-fit animate-pulse text-sm text-slate-500">
                        AIVA is researching...
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t flex gap-2">
                <input 
                    className="flex-1 border rounded-full px-6 py-2 outline-none focus:ring-2 focus:ring-[#002C5F]"
                    placeholder="Type technical query..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                />
                <Button onClick={handleSend} className="rounded-full bg-[#002C5F] hover:bg-[#001a3a]">
                    <Send className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

export default PrecorChat;